import { modelAttribute } from 'components/framework/modelBuilder';
import type ApplicationModel from '../../ApplicationModel';
import Fragment from '../../Fragment';
import {
  type Epic,
  type Feature,
  Iteration,
  type Requirement,
  type User,
  type WorkflowStatus,
} from '../../models';
import {
  type RecordEventRaw,
  type RecordEventTimeSeries,
  type RecordEventTimeSeriesGroupEnum,
  type RecordEventValueOperationEnum,
  type RecordId,
} from '../../models/generated/types';
import { orZero } from 'javascripts/util/orZero';

export type IterationStartEnd = {
  startEvent?: RecordEventRaw;
  endEvent?: RecordEventRaw;
};

const EVENTS_PER_PAGE = 2000;

function recordEventTimeSeriesFragment(
  filters: object,
  timeGroup: RecordEventTimeSeriesGroupEnum['type'],
  aggregation: RecordEventValueOperationEnum['type']
) {
  return new Fragment()
    .fragment(
      new Fragment('recordEvents')
        .argument('filters', filters, 'RecordEventFilters!')
        .fragment(
          new Fragment('timeSeries')
            .argument('timeGroup', timeGroup, true)
            .argument('aggregation', aggregation, true)
            .fragment(new Fragment('seriesRange').attr('from', 'to'))
            .attr(
              'eventType',
              'eventTimeIndex',
              'units',
              'originalEstimate',
              'remainingEstimate'
            )
        )
    )
    .resolve();
}

async function iterationStartEnd(
  iterationId: RecordId
): Promise<IterationStartEnd> {
  const eventSubject = {
    id: iterationId,
    typename: Iteration.typename,
  };

  const fragment = new Fragment()
    .fragment(
      new Fragment('recordEvents')
        .alias('startEvent')
        .argument(
          'filters',
          {
            eventSubject,
            eventType: aha.enums.RecordEventTypeEnum.ITERATION_START,
          },
          'RecordEventFilters!'
        )
        .fragment(
          new Fragment('raw').attr(
            'createdAt',
            'originalEstimate',
            'remainingEstimate'
          )
        )
    )
    .fragment(
      new Fragment('recordEvents')
        .alias('endEvent')
        .argument(
          'filters',
          {
            eventSubject,
            eventType: aha.enums.RecordEventTypeEnum.ITERATION_COMPLETED,
          },
          'RecordEventFilters!'
        )
        .fragment(
          new Fragment('raw').attr(
            'createdAt',
            'originalEstimate',
            'remainingEstimate'
          )
        )
    )
    .resolve();

  const data = await aha.graphQuery<{
    startEvent: { raw: RecordEventRaw[] };
    endEvent: { raw: RecordEventRaw[] };
  }>(fragment);

  return {
    startEvent: data.startEvent.raw[0],
    endEvent: data.endEvent.raw[0],
  };
}

async function recordEventTimeSeries(
  filters: object,
  timeGroup: RecordEventTimeSeriesGroupEnum['type'],
  aggregation: RecordEventValueOperationEnum['type']
) {
  const fragment = recordEventTimeSeriesFragment(
    filters,
    timeGroup,
    aggregation
  );
  const data = await aha.graphQuery<{
    recordEvents: { timeSeries: RecordEventTimeSeries[] };
  }>(fragment);
  return data.recordEvents.timeSeries;
}

function recordEventsRawFragment(filters: object) {
  return new Fragment().fragment(
    new Fragment('recordEvents')
      .argument('filters', filters, 'RecordEventFilters!')
      .attr('rawPageCount')
      .fragment(
        new Fragment('raw').attr(
          'createdAt',
          'eventType',
          'eventSubjectType',
          'eventSubjectId',
          'originalEstimate',
          'remainingEstimate',
          'units',
          'assignedToUserId',
          'iterationId',
          'teamWorkflowStatusId',
          'workDone'
        )
      )
      .fragment(
        new Fragment('epics').attr('id', 'name', 'referenceNum', 'path')
      )
      .fragment(
        new Fragment('features').attr(
          'id',
          'name',
          'referenceNum',
          'path',
          'showFeatureRemainingEstimate'
        )
      )
      .fragment(
        new Fragment('requirements')
          .attr('id', 'name', 'referenceNum', 'path')
          .fragment(
            new Fragment('feature').attr('id', 'showFeatureRemainingEstimate')
          )
      )
      .fragment(
        new Fragment('users').attr('id', 'avatarUrl', 'initials', 'name')
      )
      .fragment(
        new Fragment('iterations')
          .attr('id', 'name', 'status', 'startDate', 'duration')
          .fragment(new Fragment('project').attr('id'))
      )
      .fragment(
        new Fragment('workflowStatuses').attr(
          'id',
          'internalMeaning',
          'name',
          'color'
        )
      )
  );
}

type BurndownEventSubjectType =
  | 'Iteration'
  | 'Epic'
  | 'Feature'
  | 'Requirement';

type BurndownRecordAssociations = {
  users: User[];
  iterations: Iteration[];
  workflowStatuses: WorkflowStatus[];
  epics: Epic[];
  features: Feature[];
  requirements: Requirement[];
};
type BurndownRecordAssociationType = keyof BurndownRecordAssociations;

type BurndownRecordEvents = BurndownRecordAssociations & {
  raw: RecordEventRaw[];
  rawPageCount?: number;
};

const associationMappings = {
  assignedToUserId: 'users',
  teamWorkflowStatusId: 'workflowStatuses',
  iterationId: 'iterations',
} as const;

const eventSubjectTypeMappings: Record<
  BurndownEventSubjectType,
  BurndownRecordAssociationType
> = {
  Iteration: 'iterations',
  Epic: 'epics',
  Feature: 'features',
  Requirement: 'requirements',
} as const;

type BurndownMappingType = keyof typeof associationMappings;

function injectRecord(
  recordEvents: BurndownRecordEvents,
  row: RecordEventRaw,
  field: BurndownMappingType | 'eventSubjectId',
  association: BurndownRecordAssociationType
) {
  const id = row[field];

  let record: ApplicationModel | null = null;
  if (id) {
    const collection = recordEvents[association] as ApplicationModel[];
    record = collection?.find(r => r.id === id) ?? null;
  }

  if (record) {
    row[field.replace(/Id$/, '')] = modelAttribute(record);
  }
}

/**
 * This method takes the record events with their associated record IDs, then injects
 * the actual records from the side-loaded collection of records. For example, if a
 * record event has a `featureId`, it will inject the feature with that ID from the
 * `features` collection. This is to reduce the size and complexity of the GQL data.
 *
 * @private Only exported for testing.
 */
export function injectAssociatedRecords(recordEvents: BurndownRecordEvents) {
  recordEvents.raw.forEach(row => {
    // Inject the user/status mappings
    Object.entries(associationMappings).forEach(([field, collectionName]) => {
      injectRecord(
        recordEvents,
        row,
        field as BurndownMappingType,
        collectionName
      );
    });

    // Inject the eventSubject record
    if (row.eventSubjectType && row.eventSubjectId) {
      const association =
        eventSubjectTypeMappings[
          row.eventSubjectType as BurndownEventSubjectType
        ];
      injectRecord(recordEvents, row, 'eventSubjectId', association);
    }
  });
}
async function fetchEventPage(
  filters: object,
  page: number
): Promise<{ raw: RecordEventRaw[]; rawPageCount: number }> {
  const pagination = { page, per: EVENTS_PER_PAGE };
  const fragment = recordEventsRawFragment({ ...filters, ...pagination });
  const { recordEvents } = await aha.graphQuery<{
    recordEvents: BurndownRecordEvents;
  }>(fragment);

  injectAssociatedRecords(recordEvents);

  return {
    raw: recordEvents.raw,
    rawPageCount: orZero(recordEvents.rawPageCount),
  };
}

async function recordEventsRaw(filters: object) {
  // Fetch 2 pages first, then fetch the rest using MAX_CONCURRENT_PAGES. I've
  // chosen these numbers after looking at our teams and the defaults for the
  // reports. We don't know how many pages there will be until the first request
  // is finished. An empty request is fast. This balances load with common case
  // performance.
  const INITIAL_CONCURRENT_PAGES = 2;
  const MAX_CONCURRENT_PAGES = 3;
  let rawEvents: RecordEventRaw[] = [];
  let page = 1;
  let pagesLeft: number | null = null;

  while (pagesLeft === null || pagesLeft > 0) {
    const pagesToFetch =
      pagesLeft === null
        ? INITIAL_CONCURRENT_PAGES
        : Math.min(MAX_CONCURRENT_PAGES, pagesLeft);
    const results = await Promise.all(
      Array.from({ length: pagesToFetch }, (_, i) =>
        fetchEventPage(filters, page + i)
      )
    );
    rawEvents = rawEvents.concat(...results.map(r => r.raw));
    if (pagesLeft === null) {
      pagesLeft = results[0].rawPageCount - 2;
    } else {
      pagesLeft -= pagesToFetch;
    }
    page += pagesToFetch;
  }

  return rawEvents;
}

export default { iterationStartEnd, recordEventTimeSeries, recordEventsRaw };
